import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { CustomerBooking, TeamWithLeague, GroupMemberWithGroup, emptyForm } from "@/components/admin/customers/types";
// TASK-66 PR-D: fetchTeamsForUser unions teams.player1/2 +
// team_members.user_id (was UPPDRAG-62 PR-D's three-source union;
// captain_id stripped — captain users still resolve via team_members).
import { fetchTeamsForUser } from "@/lib/teamMembershipQueries";
import type { CustomerListRow } from "@/lib/customerListDisplay";

export function useCustomerData(organizationId: string | undefined, {
  search,
  page,
  pageSize,
  sortBy,
  categoryFilter,
  customerTypeFilter,
  genderFilter,
  sourceFilter,
  groupFilter,
  // UPPDRAG-47: activity-window filter on customers.last_active_at.
  // Values: 'all' (no filter), 'active_30', 'active_90', 'active_365',
  // 'inactive' (last_active_at older than 365 days or NULL).
  activityFilter,
}: {
  search: string;
  page: number;
  pageSize: number;
  sortBy: string;
  categoryFilter: string;
  customerTypeFilter?: string;
  genderFilter?: string;
  sourceFilter?: string;
  groupFilter?: string;
  activityFilter?: string;
}) {
  const queryClient = useQueryClient();

  // 010/FEL-013: listan går genom get_customers_admin_list — ETT anrop som ger
  // sidan, båda aggregaten och total_count.
  //
  // Den gamla formen var `.from("customers").select("*").range(...)`. Den kunde
  // inte ge "senaste bokning" eller "obetalt": supabase-js har ingen GROUP BY,
  // PostgREST-aggregat är avstängda, och eftersom listan är serverpaginerad kan
  // en klient-join bara dekorera de 20 rader som redan hämtats — sortering och
  // filtrering på ett framräknat fält vore då omöjlig.
  //
  // `select("*")` skickade dessutom personal_number, date_of_birth och sex andra
  // känsliga fält till varje admins webbläsare. RPC:n returnerar bara de fält
  // UI:t faktiskt använder.
  const { data: listResult, isLoading } = useQuery({
    queryKey: ["admin-customers", organizationId, search, page, pageSize, sortBy, categoryFilter, customerTypeFilter, genderFilter, sourceFilter, groupFilter, activityFilter],
    queryFn: async () => {
      // Typerna i src/integrations/supabase/types.ts genereras ur PROD och
      // känner inte funktionen förrän migrationen applicerats (P1). Samma
      // cast-mönster som repot redan använder för nya RPC:er.
      const { data, error } = await (supabase.rpc as any)("get_customers_admin_list", {
        p_org: organizationId!,
        p_search: search || null,
        p_sort: sortBy || "created_desc",
        p_limit: pageSize,
        p_offset: page * pageSize,
        p_category: categoryFilter && categoryFilter !== "all" ? categoryFilter : null,
        p_customer_type: customerTypeFilter && customerTypeFilter !== "all" ? customerTypeFilter : null,
        p_gender: genderFilter && genderFilter !== "all" ? genderFilter : null,
        p_source: sourceFilter && sourceFilter !== "all" ? sourceFilter : null,
        p_group: groupFilter && groupFilter !== "all" ? groupFilter : null,
        p_activity: activityFilter && activityFilter !== "all" ? activityFilter : null,
      });
      if (error) throw error;
      const rows = (data ?? []) as unknown as CustomerListRow[];
      // total_count är samma värde på varje rad (count(*) over ()). Tom sida ⇒ 0.
      return { rows, totalCount: rows.length > 0 ? Number(rows[0].total_count) : 0 };
    },
    enabled: !!organizationId,
  });

  const customers = listResult?.rows ?? [];
  const totalCount = listResult?.totalCount ?? 0;

  const addCustomer = useMutation({
    mutationFn: async (values: typeof emptyForm) => {
      const { error } = await supabase.from("customers").insert({
        organization_id: organizationId!,
        email: values.email.trim().toLowerCase(),
        full_name: values.full_name || null,
        phone: values.phone || null,
        level: values.level,
        preferred_hand: values.preferred_hand || null,
        player_type: values.player_type || null,
        notes: values.notes || null,
        category_id: values.category_id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-customers"] });
      toast.success("Kund tillagd");
    },
    onError: (e: Error) => {
      if (e.message?.includes("duplicate")) {
        toast.error("En kund med den e-posten finns redan");
      } else {
        toast.error("Kunde inte lägga till kund");
      }
    },
  });

  const updateCustomer = useMutation({
    mutationFn: async (values: Partial<typeof emptyForm> & { id: string }) => {
      const { id, ...rest } = values;
      const { error } = await supabase
        .from("customers")
        .update({
          full_name: rest.full_name || null,
          phone: rest.phone || null,
          level: rest.level,
          preferred_hand: rest.preferred_hand || null,
          player_type: rest.player_type || null,
          category_id: rest.category_id || null,
          notes: rest.notes || null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-customers"] });
      toast.success("Kund uppdaterad");
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || "Kunde inte uppdatera"); },
  });

  return {
    customers,
    // 010: sant totalantal ur count(*) over () — ersätter heuristiken
    // `customers.length < pageSize`, som gissade fel på exakt full sista sida.
    totalCount,
    isLoading,
    addCustomer,
    updateCustomer,
  };
}

export function useCustomerDetail(
  userId: string | null | undefined,
  organizationId: string | undefined,
) {
  const { data: customerBookings = [] } = useQuery({
    queryKey: ["admin-customer-bookings", userId, organizationId],
    queryFn: async () => {
      const { data } = await supabase
        .from("bookings")
        .select("*, courts!inner(name, hall_id, halls!inner(name, organization_id))")
        .eq("user_id", userId!)
        .eq("courts.halls.organization_id", organizationId!)
        .order("start_time", { ascending: false })
        .limit(20);
      return data ?? [];
    },
    enabled: !!userId && !!organizationId,
  });

  // Stats query: total spent, booking count, cancellations
  const { data: customerStats } = useQuery({
    queryKey: ["admin-customer-stats", userId, organizationId],
    queryFn: async () => {
      const { data } = await supabase
        .from("bookings")
        .select("status, price_total, courts!inner(hall_id, halls!inner(organization_id))")
        .eq("user_id", userId!)
        .eq("courts.halls.organization_id", organizationId!);
      const rows = data ?? [];
      const confirmed = rows.filter((b) => b.status === "confirmed" || b.status === "completed");
      const cancelled = rows.filter((b) => b.status === "cancelled");
      const totalSpent = confirmed.reduce((sum: number, b) => sum + (b.price_total || 0), 0);
      return {
        totalBookings: confirmed.length,
        cancellations: cancelled.length,
        totalSpent,
      };
    },
    enabled: !!userId && !!organizationId,
  });

  const { data: customerTeams = [] } = useQuery({
    queryKey: ["admin-customer-teams", userId],
    queryFn: async () => {
      if (!userId) return [];
      // TASK-66 PR-D: union teams.player1/2 ∪ team_members.user_id (captain
      // users resolve via team_members branch; captain_id stripped).
      return await fetchTeamsForUser<TeamWithLeague>(supabase, userId, {
        columns: "*, leagues(name)",
        limit: 10,
      });
    },
    enabled: !!userId,
  });

  const { data: customerGroups = [] } = useQuery({
    queryKey: ["admin-customer-groups", userId],
    queryFn: async () => {
      const { data } = await supabase
        .from("group_members")
        .select("group_id, status, training_groups(name, color, seasons(name))")
        .eq("player_id", userId!)
        .eq("status", "active");
      return data ?? [];
    },
    enabled: !!userId,
  });

  return {
    customerBookings: customerBookings as CustomerBooking[],
    customerStats,
    customerTeams: customerTeams as TeamWithLeague[],
    customerGroups: customerGroups as GroupMemberWithGroup[],
  };
}
